/**
 * The property-management back office's route table (C9 §5.5).
 *
 * `src/router/index.ts` is shared by every module and belongs to the wiring
 * packet, so this file declares the records and the router spreads `PMS_ROUTES`
 * in. Keeping them here means the paths, the permissions and the components
 * that implement them are decided in one place by whoever owns the module.
 *
 * Every `:id` segment carries the `\d+` constraint, so no literal word can ever
 * be parsed as a document id, and each family's list route is declared before
 * its detail route so the ordering says the same thing the constraint enforces.
 *
 * **This file covers the commercial half only.** The front desk, folios, the
 * night audit, housekeeping, channels, ancillary services and guest services are
 * the operations half and are declared beside their own screens; folding both
 * into one table would make whichever packet landed second the owner of the
 * other's paths.
 *
 * Which permission guards which screen follows the API rather than the topic:
 * the rate-plan editor is `pms.rate.read` because that is what
 * `GET /pms-rate-plans/:id` declares — *editing* the grid needs
 * `pms.rate.manage` and *activating* the plan needs `pms.rate.publish`, and the
 * screen checks each separately.
 */

import type { RouteRecordRaw } from 'vue-router'

import { PMS_PERMISSIONS } from '@/modules/pms/permissions'

export const PMS_ROUTES: RouteRecordRaw[] = [
  {
    path: '/pms/properties',
    name: 'pms-properties',
    component: () => import('@/modules/pms/PropertiesListView.vue'),
    meta: { titleKey: 'pms.routeTitles.properties', permission: PMS_PERMISSIONS.propertyRead },
  },
  {
    path: '/pms/properties/:id(\\d+)',
    name: 'pms-property',
    component: () => import('@/modules/pms/PropertyView.vue'),
    meta: { titleKey: 'pms.routeTitles.property', permission: PMS_PERMISSIONS.propertyRead },
  },
  {
    path: '/pms/settings',
    name: 'pms-settings',
    component: () => import('@/modules/pms/PmsSettingsView.vue'),
    meta: { titleKey: 'pms.routeTitles.settings', permission: PMS_PERMISSIONS.propertyRead },
  },
  {
    path: '/pms/rate-plans',
    name: 'pms-rate-plans',
    component: () => import('@/modules/pms/RatePlansListView.vue'),
    meta: { titleKey: 'pms.routeTitles.ratePlans', permission: PMS_PERMISSIONS.rateRead },
  },
  {
    path: '/pms/rate-plans/:id(\\d+)',
    name: 'pms-rate-plan',
    component: () => import('@/modules/pms/RatePlanView.vue'),
    meta: { titleKey: 'pms.routeTitles.ratePlan', permission: PMS_PERMISSIONS.rateRead },
  },
  {
    path: '/pms/restrictions',
    name: 'pms-restrictions',
    component: () => import('@/modules/pms/RestrictionCalendarView.vue'),
    meta: { titleKey: 'pms.routeTitles.restrictions', permission: PMS_PERMISSIONS.rateRead },
  },
  {
    path: '/pms/availability',
    name: 'pms-availability',
    component: () => import('@/modules/pms/AvailabilityCalendarView.vue'),
    meta: { titleKey: 'pms.routeTitles.availability', permission: PMS_PERMISSIONS.availabilityRead },
  },
  {
    path: '/pms/reservations',
    name: 'pms-reservations',
    component: () => import('@/modules/pms/ReservationsListView.vue'),
    meta: { titleKey: 'pms.routeTitles.reservations', permission: PMS_PERMISSIONS.reservationRead },
  },
  {
    path: '/pms/reservations/:id(\\d+)',
    name: 'pms-reservation',
    component: () => import('@/modules/pms/ReservationView.vue'),
    meta: { titleKey: 'pms.routeTitles.reservation', permission: PMS_PERMISSIONS.reservationRead },
  },
  {
    path: '/pms/group-blocks',
    name: 'pms-group-blocks',
    component: () => import('@/modules/pms/GroupBlocksListView.vue'),
    meta: { titleKey: 'pms.routeTitles.groupBlocks', permission: PMS_PERMISSIONS.reservationRead },
  },
  {
    path: '/pms/group-blocks/:id(\\d+)',
    name: 'pms-group-block',
    component: () => import('@/modules/pms/GroupBlockView.vue'),
    meta: { titleKey: 'pms.routeTitles.groupBlock', permission: PMS_PERMISSIONS.reservationRead },
  },
  {
    path: '/pms/guests',
    name: 'pms-guests',
    component: () => import('@/modules/pms/GuestsListView.vue'),
    meta: { titleKey: 'pms.routeTitles.guests', permission: PMS_PERMISSIONS.guestRead },
  },
  {
    path: '/pms/guests/:id(\\d+)',
    name: 'pms-guest',
    component: () => import('@/modules/pms/GuestView.vue'),
    meta: { titleKey: 'pms.routeTitles.guest', permission: PMS_PERMISSIONS.guestRead },
  },
]

/** The same set as strings, for links inside the module. */
export const PMS_PATHS = {
  properties: '/pms/properties',
  property: (id: number) => `/pms/properties/${id}`,
  settings: '/pms/settings',
  ratePlans: '/pms/rate-plans',
  ratePlan: (id: number) => `/pms/rate-plans/${id}`,
  restrictions: '/pms/restrictions',
  availability: '/pms/availability',
  reservations: '/pms/reservations',
  reservation: (id: number) => `/pms/reservations/${id}`,
  groupBlocks: '/pms/group-blocks',
  groupBlock: (id: number) => `/pms/group-blocks/${id}`,
  guests: '/pms/guests',
  guest: (id: number) => `/pms/guests/${id}`,
} as const

/** Deep links out of the module, to screens other packets own. */
export const PMS_EXTERNAL_PATHS = {
  /** One identity per person; the guest screen links out rather than re-rendering it. */
  party: (id: number) => `/party/parties/${id}`,
  approvals: '/approvals',
} as const
